/**
 * --------------------------------------------------------------------------
 * NJ : Dropdown.js
 * --------------------------------------------------------------------------
 */

import { Data, EventHandler, SelectorEngine } from 'nj-utils';
import { coreConfig, eventsName } from 'nj-components/core.config';
import AbstractComponent from 'nj-components/AbstractComponent';
import { EVENT as COLLAPSE_EVENT } from 'nj-components/Collapse';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME            = `${coreConfig.KEY_PREFIX}-dropdown`;
const DATA_KEY        = `${coreConfig.KEY_PREFIX}.dropdown`;
const EVENT_KEY       = `.${DATA_KEY}`;
const DATA_API_KEY    = coreConfig.KEY_PREFIX;
const ESCAPE_KEYCODE  = 27; // KeyboardEvent.which value for Escape

const ATTRIBUTE = {
  content: 'data-content',
  selectedContent: 'data-selected-content',
  value: 'data-value'
};

const CLASS_NAME = {
  isFilled: `${NAME}__label--is-filled`,
  shownCollapse : `${NAME}--shown-collapse`
};

const SELECTOR = {
  default : `.${NAME}`,
  input   : 'input',
  label   : `.${NAME}__label`,
  options : `.${NAME} [${ATTRIBUTE.value}]`
};

const EVENT = {
  clickDataApi   : `${eventsName.mousedown}${EVENT_KEY}${DATA_API_KEY}`,
  keydownDismiss : `${eventsName.keydown}.dismiss${EVENT_KEY}`
};

class Dropdown extends AbstractComponent {
  constructor(element) {
    super(Dropdown, element);

    this.element = element;

    if (this.element) {
      Data.setData(element, DATA_KEY, this);
      this.addBlurEvent();
      this.addTouchEvent();
    }
  }

  addTouchEvent() {
    EventHandler
      .on(this.element, EVENT.keydownDismiss, () => {
        if (event.which === ESCAPE_KEYCODE) {
          event.preventDefault();
          this.closeMenu();
        }
      });
  }

  addBlurEvent() {
    EventHandler
      .on(this.element, 'blur', () => {
        console.log('blur'); // eslint-disable-line no-console
        this.closeMenu();
      });
  }

  closeMenu() {
    // trigger click to hide options when element has lost focus
    if (this.element.classList.contains(CLASS_NAME.shownCollapse)) {
      this.element.click();
    }
  }

  dispose() {
    Data.removeData(this.element, DATA_KEY);
    this.element = null;
  }

  static handleCollapseShow(event) {
    SelectorEngine.closest(event.target, SELECTOR.default)
      .classList.add(CLASS_NAME.shownCollapse);
  }

  static handleCollapseHide(event) {
    SelectorEngine.closest(event.target, SELECTOR.default)
      .classList.remove(CLASS_NAME.shownCollapse);
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static init(options) {
    return super.init(this, options, SELECTOR.default);
  }

  static selector = SELECTOR.default;
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */
EventHandler
  .on(document, COLLAPSE_EVENT.show, SELECTOR.default, (event) => {
    Dropdown.handleCollapseShow(event);
  });

EventHandler
  .on(document, COLLAPSE_EVENT.hidden, SELECTOR.default, (event) => {
    Dropdown.handleCollapseHide(event);
  });

EventHandler
  .on(document, EVENT.clickDataApi, SELECTOR.options, (event) => {
    // HTML elements
    const option = SelectorEngine.closest(event.target, `[${ATTRIBUTE.value}]`);
    const dropdown = SelectorEngine.closest(event.target, SELECTOR.default);
    const elemDataContent = SelectorEngine.findOne(`[${ATTRIBUTE.content}]`, option);
    const input = SelectorEngine.findOne(SELECTOR.input, dropdown);

    // Data
    const dataSelectedValue = option.getAttribute(ATTRIBUTE.value);
    const dataContent = elemDataContent ? elemDataContent.textContent : option.textContent; // get option content if 'data-content' attribute doesn't exist

    input.value = dataSelectedValue;
    dropdown.setAttribute(ATTRIBUTE.selectedContent, dataContent);
  });

export default Dropdown;
